import { useEffect, useState } from "react";
import { Copy, Download, Eye, ImageUp, Loader2, RefreshCw, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const saveText = (text, name) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
  Object.assign(document.createElement("a"), { href: url, download: name }).click();
  URL.revokeObjectURL(url);
};

export const BrandLogo = () => {
  const [url, setUrl] = useState("");
  const load = () => api.get("/admin/brand/logo", { responseType: "blob" }).then((r) => setUrl(URL.createObjectURL(r.data))).catch(() => setUrl(""));
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post("/admin/brand/logo", fd); toast.success("Logo saved. It appears on every plan PDF."); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = () => api.delete("/admin/brand/logo").then(() => { setUrl(""); toast.success("Logo removed"); });
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 rounded-xl bg-secondary p-3 text-xs" data-testid="brand-logo">
      <span className="font-medium text-navy">PDF logo</span>
      {url ? <img src={url} alt="Your logo" className="h-8 max-w-[140px] object-contain bg-white rounded px-1" data-testid="brand-logo-preview" /> : <span className="text-muted-foreground">None yet: plans show "The Roots Method"</span>}
      <label className="inline-flex items-center gap-1 cursor-pointer rounded-full border border-border bg-white px-3 py-1 hover:border-pinkb" data-testid="brand-logo-upload-label">
        <ImageUp className="h-3.5 w-3.5" />{url ? "Replace" : "Upload PNG or JPG"}
        <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={upload} data-testid="brand-logo-input" />
      </label>
      {url && <button onClick={remove} className="inline-flex items-center gap-1 text-muted-foreground hover:text-rose-600" data-testid="brand-logo-remove"><Trash2 className="h-3.5 w-3.5" />Remove</button>}
    </div>
  );
};

export const PlanDraftDialog = ({ o, onClose, onDelivered }) => {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [tab, setTab] = useState("draft");
  const [pdfUrl, setPdfUrl] = useState("");
  const [busy, setBusy] = useState("");
  const preview = async () => {
    setBusy("preview");
    try {
      await api.put(`/admin/plan-orders/${o.id}/draft`, { text });
      const r = await api.post(`/admin/plan-orders/${o.id}/pdf/preview`, null, { responseType: "blob" });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(r.data));
      setTab("preview");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const send = async () => {
    if (!window.confirm(`Send this PDF to ${o.email || "the client"} now? They'll get an email with a download link.`)) return;
    setBusy("send");
    try {
      const r = await api.post(`/admin/plan-orders/${o.id}/pdf/send`);
      toast.success("Plan delivered. The client has been emailed.");
      onDelivered?.(r.data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const load = () => api.get(`/admin/plan-orders/${o.id}/questionnaire`).then((r) => { setD(r.data); setText(r.data.draft?.text || ""); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (o) { setTab("draft"); setPdfUrl(""); load(); } }, [o]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (d?.draft?.status !== "generating") return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () => api.put(`/admin/plan-orders/${o.id}/draft`, { text }).then(() => toast.success("Draft saved")).catch((e) => toast.error(errMsg(e)));
  const regen = async () => {
    if (!window.confirm("Create a fresh AI draft? This replaces the current draft, including your edits.")) return;
    try { await api.post(`/admin/plan-orders/${o.id}/draft/regenerate`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const name = (o?.business_name || o?.user_name || "client").replace(/[^\w-]+/g, "-").toLowerCase();
  const st = d?.draft?.status;
  return (
    <Dialog open={!!o} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="plan-draft-dialog">
        <DialogHeader><DialogTitle>{o?.user_name} · {o?.business_name || o?.email}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : !d.submitted_at ? (
          <div className="text-sm space-y-3" data-testid="plan-draft-waiting"><p>The questionnaire hasn't been sent yet. You can send the client their link again:</p>
            <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs break-all rounded bg-secondary px-2 py-1">{window.location.origin + d.link}</span>
              <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => navigator.clipboard.writeText(window.location.origin + d.link).then(() => toast.success("Link copied"))} data-testid="plan-link-copy"><Copy className="h-3.5 w-3.5 mr-1" />Copy link</Button></div></div>
        ) : (
          <div>
            <div className="flex gap-2 mb-4">{[["draft", "AI first draft"], ...(pdfUrl ? [["preview", "PDF preview"]] : []), ["answers", "Questionnaire answers"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`plan-draft-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm ${tab === k ? "bg-navy text-white" : "bg-secondary"}`}>{l}</button>
            ))}</div>
            {tab === "preview" && pdfUrl ? (
              <div data-testid="plan-pdf-preview">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTab("draft")} data-testid="plan-pdf-edit">Back to edit</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => Object.assign(document.createElement("a"), { href: pdfUrl, download: `${name}-mini-marketing-plan.pdf` }).click()} data-testid="plan-pdf-download"><Download className="h-3.5 w-3.5 mr-1" />Download PDF</Button>
                  <Button size="sm" onClick={send} disabled={!!busy} className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white" data-testid="plan-pdf-send">{busy === "send" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}Send to client</Button>
                </div>
                <iframe src={pdfUrl} title="Plan PDF preview" className="w-full h-[65vh] rounded-xl border border-border bg-white" data-testid="plan-pdf-frame" />
              </div>
            ) : tab === "answers" ? (
              <div>
                <Button size="sm" variant="outline" className="rounded-full mb-3" onClick={() => saveText(d.answers_text, `${name}-questionnaire.md`)} data-testid="plan-answers-download"><Download className="h-3.5 w-3.5 mr-1" />Download answers</Button>
                <pre className="whitespace-pre-wrap text-sm font-sans rounded-xl bg-secondary p-4" data-testid="plan-answers-text">{d.answers_text}</pre>
              </div>
            ) : st === "generating" ? (
              <p className="flex items-center gap-2 text-sm" data-testid="plan-draft-generating"><Loader2 className="h-4 w-4 animate-spin text-pinkb" />Drafting from your method. This takes a few minutes, and you can close this and come back.</p>
            ) : (
              <div>
                {st === "failed" && <p className="mb-3 text-sm text-rose-700" data-testid="plan-draft-failed">The draft didn't finish. Try creating it again.</p>}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button size="sm" onClick={save} className="rounded-full bg-navy text-white" data-testid="plan-draft-save"><Save className="h-3.5 w-3.5 mr-1" />Save edits</Button>
                  <Button size="sm" onClick={preview} disabled={!!busy || !text.trim()} className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white" data-testid="plan-pdf-preview-btn">{busy === "preview" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1" />}Preview PDF</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copied"))} data-testid="plan-draft-copy"><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => saveText(text, `${name}-mini-marketing-plan-draft.md`)} data-testid="plan-draft-download"><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                  <Button size="sm" variant="ghost" className="rounded-full" onClick={regen} data-testid="plan-draft-regenerate"><RefreshCw className="h-3.5 w-3.5 mr-1" />New draft</Button>
                </div>
                <Textarea rows={24} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-xs bg-white" data-testid="plan-draft-text" />
                <p className="text-xs text-muted-foreground mt-2">Edit in markdown (## for headings, - for bullets, | for tables), then Preview PDF. You can still upload your own PDF instead with "Upload finished PDF".</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
